import { useState, useEffect } from "react"
import { useLanguage } from "../contexts/LanguageContext"
import { fetchStaticPageData, mapLanguageCode, PAGE_SLUGS } from "../services/cmsApi"

export default function TermsOfUse() {
    const { t, currentLanguage } = useLanguage()
    const [content, setContent] = useState("")
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        const loadContent = async () => {
            try {
                setLoading(true)
                setError(null)

                // Always use current language from context (responds to dropdown changes)
                const selectedLanguage = currentLanguage

                // Map the selected language to API format
                const apiLanguage = mapLanguageCode(selectedLanguage)
                console.log(`Terms of Use: Using language ${selectedLanguage} -> API language: ${apiLanguage}`)

                // Fetch data from CMS API
                const data = await fetchStaticPageData(PAGE_SLUGS.TERMS, apiLanguage)
                console.log('Terms API Response:', data?.data?.pages)
                console.log('Available pages:', data?.data?.pages?.map(p => ({ slug: p.page_slug, type: p.page_type })))

                // Extract content from API response
                if (data && data.data && data.data.pages && Array.isArray(data.data.pages)) {
                    // Combine all pages content
                    const allPagesContent = data.data.pages
                        .filter(page => page.content) // Only pages with content
                        .map(page => page.content)
                        .join('<hr style="margin: 2rem 0; border: 1px solid #e5e7eb;">') // Separator between pages

                    if (allPagesContent) {
                        setContent(allPagesContent)
                        console.log(`Loaded ${data.data.pages.length} pages for Terms of Use in ${selectedLanguage}`)
                    } else {
                        setContent(t("policy.terms.content"))
                    }
                } else if (data && data.content) {
                    // Fallback for old API structure
                    setContent(data.content)
                } else {
                    // Fallback to translation file if API doesn't return content
                    setContent(t("policy.terms.content"))
                }
            } catch (err) {
                console.error('Error loading terms content:', err)
                setError(err.message)
                // Fallback to translation file on error
                setContent(t("policy.terms.content"))
            } finally {
                setLoading(false)
            }
        }

        loadContent()
    }, [currentLanguage, t]) // Reacts to language changes from dropdown

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bg-white rounded-lg shadow-lg p-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-8">
                    {t("footer.termsOfUse")}
                </h1>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8500]"></div>
                        <span className="ml-3 text-gray-600">Loading terms of use...</span>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                    Error loading content
                                </h3>
                                <div className="mt-2 text-sm text-red-700">
                                    <p>Unable to fetch the latest terms of use. Showing cached content.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="prose max-w-none text-gray-700">
                    <div dangerouslySetInnerHTML={{ __html: content }} />
                </div>
            </div>

            {/* Back to Home Button */}
            <div className="mt-8 text-center">
                <a
                    href="/"
                    className="inline-flex items-center px-6 py-3 bg-[#FF8500] text-white font-medium rounded-lg hover:bg-[#FF6B00] transition-colors"
                >
                    ← Back to Home
                </a>
            </div>
        </div>
    )
}
